// Crop and shrink photos in the browser before they're uploaded, so phones'
// 12-megapixel photos become small, fast images and never hit storage limits.

export interface PixelArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageOutputOptions {
  /** Longest side of the result, in pixels. */
  maxSize: number;
  /** Largest file allowed; quality and then size are reduced until it fits. */
  maxBytes: number;
  /** Base name for the resulting file (extension is added). */
  name?: string;
}

/** Scale (w, h) down to fit inside max×max, keeping the shape. Never scales up. */
export function fitWithin(width: number, height: number, max: number) {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** PNGs may be transparent (logos, diagrams), so they stay PNG; everything else becomes JPEG. */
export function outputType(inputType: string): 'image/png' | 'image/jpeg' {
  return inputType === 'image/png' ? 'image/png' : 'image/jpeg';
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("That file couldn't be opened as an image."));
    img.src = src;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("The image couldn't be processed."))), type, quality),
  );
}

/**
 * Draw (the cropped part of) an image at most maxSize on its longest side and
 * encode it, lowering JPEG quality and then the size until it is under maxBytes.
 */
export async function renderImage(img: HTMLImageElement, inputType: string, area: PixelArea | null, opts: ImageOutputOptions): Promise<File> {
  const src = area ?? { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
  const type = outputType(inputType);
  let max = opts.maxSize;
  for (let attempt = 0; attempt < 6; attempt++) {
    const size = fitWithin(src.width, src.height, max);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("The image couldn't be processed.");
    if (type === 'image/jpeg') {
      // JPEG has no transparency: put transparent areas on white, not black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size.width, size.height);
    }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, src.x, src.y, src.width, src.height, 0, 0, size.width, size.height);
    for (const quality of type === 'image/jpeg' ? [0.85, 0.72, 0.6] : [undefined]) {
      const blob = await toBlob(canvas, type, quality);
      if (blob.size <= opts.maxBytes) {
        const ext = type === 'image/png' ? 'png' : 'jpg';
        return new File([blob], `${opts.name ?? 'image'}.${ext}`, { type });
      }
    }
    max = Math.round(Math.max(size.width, size.height) * 0.75);
  }
  throw new Error('That image is too large. Try a smaller one.');
}
